import { useState, useMemo } from 'react';
import { VentaActividad } from '../lib/supabase';
import { Miembro } from '../lib/supabase';
import { getNombreCliente, esClienteExterno } from '../utils/clienteHelpers';
import { matchesSearch } from '../utils/textNormalization';

interface UsePedidosFilterProps {
  pedidos: VentaActividad[];
  miembros: Miembro[];
}

interface FilterState {
  tipo: 'todos' | 'miembros' | 'externos';
  busqueda: string;
  estadosPago: string[];
  estadosEntrega: string[];
  formasEntrega: string[];
}

export const usePedidosFilter = ({ pedidos, miembros }: UsePedidosFilterProps) => {
  const [filters, setFilters] = useState<FilterState>({
    tipo: 'todos',
    busqueda: '',
    estadosPago: [],
    estadosEntrega: [],
    formasEntrega: []
  });

  // Funciones para actualizar los filtros
  const setTipo = (tipo: FilterState['tipo']) => {
    setFilters(prev => ({ ...prev, tipo }));
  };

  const setBusqueda = (busqueda: string) => {
    setFilters(prev => ({ ...prev, busqueda }));
  };

  const toggleEstadoPago = (estado: string) => {
    setFilters(prev => ({
      ...prev,
      estadosPago: prev.estadosPago.includes(estado)
        ? [] // Si ya está activo, desactivar todos
        : [estado] // Si no está activo, activar solo este
    }));
  };

  const toggleEstadoEntrega = (estado: string) => {
    setFilters(prev => ({
      ...prev,
      estadosEntrega: prev.estadosEntrega.includes(estado)
        ? [] // Si ya está activo, desactivar todos
        : [estado] // Si no está activo, activar solo este
    }));
  };

  const toggleFormaEntrega = (forma: string) => {
    setFilters(prev => ({
      ...prev,
      formasEntrega: prev.formasEntrega.includes(forma)
        ? [] // Si ya está activo, desactivar todos
        : [forma] // Si no está activo, activar solo este
    }));
  };

  const resetFilters = () => {
    setFilters({
      tipo: 'todos',
      busqueda: '',
      estadosPago: [],
      estadosEntrega: [],
      formasEntrega: []
    });
  };

  // Calcular pedidos filtrados
  const pedidosFiltrados = useMemo(() => {
    let filtrados = [...pedidos];

    // Filtrar por tipo (miembro/externo)
    if (filters.tipo === 'miembros') {
      filtrados = filtrados.filter(p => !esClienteExterno(p));
    } else if (filters.tipo === 'externos') {
      filtrados = filtrados.filter(p => esClienteExterno(p));
    }

    // Filtrar por estados de pago
    if (filters.estadosPago.length > 0) {
      filtrados = filtrados.filter(p => {
        // Si el filtro es 'no_pagado', incluir pendiente y pago_parcial
        if (filters.estadosPago.includes('no_pagado')) {
          return p.estado_pago === 'pendiente' || p.estado_pago === 'pago_parcial';
        }
        return filters.estadosPago.includes(p.estado_pago);
      });
    }

    // Filtrar por estados de entrega
    if (filters.estadosEntrega.length > 0) {
      filtrados = filtrados.filter(p => {
        // Si el filtro es 'no_entregado', incluir pendiente y entrega_parcial
        if (filters.estadosEntrega.includes('no_entregado')) {
          return p.estado_entrega === 'pendiente' || p.estado_entrega === 'entrega_parcial';
        }
        return filters.estadosEntrega.includes(p.estado_entrega);
      });
    }

    // Filtrar por forma de entrega
    if (filters.formasEntrega.length > 0) {
      filtrados = filtrados.filter(p => filters.formasEntrega.includes(p.forma_entrega));
    }

    // Filtrar por búsqueda
    if (filters.busqueda) {
      const { busqueda } = filters;
      filtrados = filtrados.filter(p => matchesSearch(getNombreCliente(p, miembros), busqueda));
    }

    // Ordenamiento inteligente con prioridad por pendientes
    const tienePendiente = (p: VentaActividad) =>
      !p.anulado && (p.estado_pago !== 'pagado' || p.estado_entrega !== 'entregado');

    filtrados.sort((a, b) => {
      // 0. Prioridad: pedidos con pago o entrega pendiente van primero
      const aPendiente = tienePendiente(a);
      const bPendiente = tienePendiente(b);
      if (aPendiente && !bPendiente) return -1;
      if (!aPendiente && bPendiente) return 1;

      // 1. Primero por fecha de entrega (si ambos tienen)
      if (a.fecha_entrega && b.fecha_entrega) {
        const fechaA = new Date(a.fecha_entrega).getTime();
        const fechaB = new Date(b.fecha_entrega).getTime();
        if (fechaA !== fechaB) return fechaA - fechaB;
        
        // 2. Si tienen la misma fecha, ordenar por rango horario
        if (a.rango_horario_retiro_despacho && b.rango_horario_retiro_despacho) {
          return a.rango_horario_retiro_despacho.localeCompare(b.rango_horario_retiro_despacho);
        }
      }
      
      // 3. Los que tienen fecha de entrega van primero
      if (a.fecha_entrega && !b.fecha_entrega) return -1;
      if (!a.fecha_entrega && b.fecha_entrega) return 1;
      
      // 4. Si ninguno tiene fecha de entrega, ordenar por fecha de creación (más reciente primero)
      const fechaVentaA = new Date(a.fecha_venta).getTime();
      const fechaVentaB = new Date(b.fecha_venta).getTime();
      return fechaVentaB - fechaVentaA;
    });

    return filtrados;
  }, [pedidos, miembros, filters]);

  // Verificar si hay filtros activos
  const hasActiveFilters = filters.tipo !== 'todos' || 
                          filters.busqueda !== '' || 
                          filters.estadosPago.length > 0 || 
                          filters.estadosEntrega.length > 0 ||
                          filters.formasEntrega.length > 0;

  return {
    filters,
    pedidosFiltrados,
    hasActiveFilters,
    setTipo,
    setBusqueda,
    toggleEstadoPago,
    toggleEstadoEntrega,
    toggleFormaEntrega,
    resetFilters
  };
};
